import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { authAPI } from '../lib/auth-api';
import { ProfileCompletionModal } from './ProfileCompletionModal';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, customer, isAuthenticated } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showHelpBadge, setShowHelpBadge] = useState(false);
  const queryClient = useQueryClient();

  // Check if profile needs completion
  const { data: profileStatus } = useQuery({
    queryKey: ['profile', 'completion'],
    queryFn: () => authAPI.checkProfileComplete(),
    enabled: isAuthenticated && !!user,
    retry: false,
  });

  // Show modal if profile needs completion
  useEffect(() => {
    if (profileStatus?.needsProfileCompletion) {
      setShowProfileModal(true);
    }
  }, [profileStatus]);

  const handleProfileComplete = async () => {
    // Refresh user data
    await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    await queryClient.invalidateQueries({ queryKey: ['profile', 'completion'] });
    setShowProfileModal(false);

    // Draw attention to the Quick Start Guide and Help nav item for a few seconds
    setShowHelpBadge(true);
    setTimeout(() => setShowHelpBadge(false), 6000);
    window.dispatchEvent(new CustomEvent('varinym:profile-completed'));
  };

  const handleLogout = () => {
    authAPI.logout();
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const navigation = [
    { name: 'Dashboard', href: '/' },
    { name: 'Identities', href: '/identities' },
    { name: 'Contexts', href: '/contexts' },
    { name: 'Bulk Operations', href: '/bulk' },
    ...(isAdmin ? [{ name: 'Data Requests', href: '/dsr' }] : []),
    { name: 'Admin', href: '/settings' },
    { name: 'Help', href: '/help' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation - Dark Theme */}
      <nav className="bg-[hsl(var(--nav-background))] border-b border-[hsl(var(--nav-border))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between">
            <div className="flex">
              <div className="flex flex-shrink-0 items-center">
                <img src="/varinym-logo.png" alt="Varinym" className="h-[1.2em]" />
              </div>
              <div className="hidden sm:ml-8 sm:flex sm:space-x-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href ||
                    (item.href !== '/' && location.pathname.startsWith(item.href));
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`link-bare relative inline-flex items-center px-4 py-4 text-sm font-medium border-b-2 transition-colors ${
                        isActive
                          ? 'border-primary text-[hsl(var(--nav-foreground))]'
                          : 'border-transparent text-[hsl(var(--nav-muted))] hover:text-[hsl(var(--nav-foreground))] hover:border-[hsl(var(--nav-border))]'
                      }`}
                    >
                      {item.name}
                      {item.name === 'Help' && showHelpBadge && (
                        <span className="absolute top-2 right-0 w-2 h-2 rounded-full bg-primary animate-pulse" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:items-center gap-4">
              <div className="hidden lg:block text-sm min-w-0">
                <div className="font-medium text-[hsl(var(--nav-foreground))] truncate max-w-[220px]">{user?.email}</div>
                <div className="text-xs text-[hsl(var(--nav-muted))] truncate max-w-[220px]">{customer?.name}</div>
              </div>
              <button
                onClick={handleLogout}
                className="rounded-md bg-[hsl(var(--nav-border))] px-3 py-2 text-sm font-medium text-[hsl(var(--nav-foreground))] hover:bg-[hsl(var(--nav-border))]/80 transition-colors"
              >
                Logout
              </button>
            </div>
            <div className="-mr-2 flex items-center sm:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center rounded-md p-2 text-[hsl(var(--nav-muted))] hover:bg-[hsl(var(--nav-border))] hover:text-[hsl(var(--nav-foreground))]"
              >
                <span className="sr-only">Open main menu</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu - Dark Theme */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-[hsl(var(--nav-border))]">
            <div className="space-y-1 pb-3 pt-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`link-bare relative block border-l-4 py-2 pl-3 pr-4 text-base font-medium ${
                      isActive
                        ? 'border-primary bg-[hsl(var(--nav-border))] text-[hsl(var(--nav-foreground))]'
                        : 'border-transparent text-[hsl(var(--nav-muted))] hover:border-[hsl(var(--nav-border))] hover:bg-[hsl(var(--nav-border))]/50 hover:text-[hsl(var(--nav-foreground))]'
                    }`}
                  >
                    {item.name}
                    {item.name === 'Help' && showHelpBadge && (
                      <span className="absolute top-2 left-1 w-2 h-2 rounded-full bg-primary animate-pulse" />
                    )}
                  </Link>
                );
              })}
              <button
                onClick={handleLogout}
                className="block w-full text-left border-l-4 border-transparent py-2 pl-3 pr-4 text-base font-medium text-[hsl(var(--nav-muted))] hover:border-[hsl(var(--nav-border))] hover:bg-[hsl(var(--nav-border))]/50 hover:text-[hsl(var(--nav-foreground))]"
              >
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Developer/free-tier warning strip */}
      {user?.subscription?.tier === 'developer' && (
        <div className="bg-warning/[0.14] border-b border-border">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-2.5">
            <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0" />
            <p className="text-[13px] text-foreground">
              You're on the <strong>Developer</strong> tier - use for development and trial purposes only, not intended
              for production use.{' '}
              <Link to="/billing/plans" className="text-warning font-semibold underline underline-offset-2">
                Upgrade your plan →
              </Link>
            </p>
          </div>
        </div>
      )}

      {/* Main content - Light Theme */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">{children}</main>

      {/* Profile Completion Modal */}
      <ProfileCompletionModal isOpen={showProfileModal} onComplete={handleProfileComplete} />
    </div>
  );
}
